import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { PaymentModal } from '../../components/payment/PaymentModal'
import { CashPaymentModal } from '../../components/payment/CashPaymentModal'
import { Button } from '../../components/atom/Button'

import {
  AffiliateBenefit,
  CouponBenefit,
  DefaultBenefit,
  InviteBenefit,
  PaymentOrderRequest,
} from '../../service/payment'

import useGetAutoChargeList from '../../hooks/query/point/useGetAutoChargeList'
import { useGetMyPoint } from '../../hooks/query/point/useGetMyPoint'
import { RequestPurchasePoint } from '../../service/point'
import { useGetPointOrderId } from '../../hooks/query/point/usetGetPoinOrderId'
import { PromotionBanner } from '../../components/affiliate/PromotionBanner'

import useUserStore from '../../store/useUserStore'
import usePrevStore from '../../store/usePrevStore'
import { savePrevPath } from '../../utils/common'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'

export const StyleRoomPriceGuide = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const payment = params.get('payment')
  const { data: myPointData } = useGetMyPoint()
  const { mutate: pointOrderIdMutate } = useGetPointOrderId()

  const userInfo = useUserStore((state: any) => state.userInfo)
  const prevSet = usePrevStore((state: any) => state.set)
  const navigate = useNavigate()

  const { data: autoChargeList } = useGetAutoChargeList()
  const [paymentModal, setPaymentModal] = useState<boolean>(false)
  const [isCashPaymentModal, setCashPaymentModal] = useState<boolean>(false)
  const [requestPurchasePoint, setRequestPurchasePoint] =
    useState<RequestPurchasePoint>({ orderId: '' })
  const [paymentOrderRequest, setPaymentOrderRequest] =
    useState<PaymentOrderRequest>({
      orderId: '',
      productName: '',
      subscriptionType: '',
      price: 0,
      vat: 0,
      id: 0,
    })
  const [affiliateBenefit, setAffiliateBenefit] =
    useState<null | AffiliateBenefit>(null)
  const [couponBenefit, setCouponBenefit] = useState<null | CouponBenefit>(null)
  const [defaultBenefit, setDefaultBenefit] = useState<null | DefaultBenefit>(
    null,
  )
  const [inviteBenefit, setInviteBenefit] = useState<null | InviteBenefit>(null)

  const handleCashPaymentModal = () => {
    if (userInfo) {
      pointOrderIdMutate(undefined, {
        onSuccess: (data) => {
          setRequestPurchasePoint(data)
          setCashPaymentModal(true)
        },
        onError: () => {},
      })
    } else {
      savePrevPath('/price/style-room?payment=true', prevSet)
      navigate('/sign-up')
    }
  }

  useEffect(() => {
    if (paymentModal) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [paymentModal])

  useEffect(() => {
    if (payment && userInfo) {
      params.delete('payment')
      const updatedSearch = params.toString()
      const updatedPath = `${location.pathname}${
        updatedSearch ? `?${updatedSearch}` : ''
      }`
      navigate(updatedPath, { replace: true })
      handleCashPaymentModal()
    }
  }, [payment, userInfo])

  return (
    <>
      <Helmet>
        <title>스타일룸 가격 안내</title>
        <meta
          name="description"
          content="AI 스타일룸으로 전문적인 상품 이미지와 영상을 생성해보세요."
        />
        <link
          rel="canonical"
          href="https://fastcut.fastseller.shop/price/style-room"
        />
      </Helmet>
      <div className="mx-auto my-0 flex max-w-[375px] flex-col items-center gap-6 md:max-w-[1000px] md:gap-6">
        {userInfo?.affiliate.point && (
          <PromotionBanner
            daysLeft={userInfo?.affiliate?.point?.daysLeft}
            hoursLeft={userInfo?.affiliate?.point?.hoursLeft}
            affiliateImage={userInfo?.affiliate?.point?.affiliateImage}
            affiliateDescription={
              userInfo?.affiliate?.point?.affiliateDescription
            }
          />
        )}

        {/* Header Section */}
        <div className="flex w-[343px] flex-col items-center md:w-full">
          <h1 className="text-[32px] font-bold text-[#2E343D] md:text-[40px]">
            스타일룸 가격 안내
          </h1>

          {/* Promotion Banner */}
          <div className="mt-6 flex w-full max-w-[460px] px-4 md:px-0">
            <div
              className="flex w-full items-center justify-center gap-1 rounded-[100px] px-2 py-1.5 md:gap-2"
              style={{
                background: 'linear-gradient(to right, #6095FF, #D512E7)',
              }}
            >
              <span className="text-[14px] font-bold text-white md:text-[16px]">
                서비스 오픈 기념
              </span>
              <span className="text-[14px] font-bold text-white md:text-[16px]">
                🌟 90% 할인 🌟
              </span>
              <span className="text-[14px] font-bold text-white md:text-[16px]">
                ~11월 30일
              </span>
            </div>
          </div>
        </div>

        {/* Main Services Section */}
        <div className="flex w-[343px] flex-col gap-4 rounded-[20px] bg-[#F6F8FB] p-5 md:w-[1000px] md:flex-row md:gap-4 md:p-8">
          {/* Style Shot Section */}
          <div className="flex flex-1 flex-col gap-3 md:gap-4">
            <h2 className="text-[16px] font-bold text-[#2E343D] md:text-[18px]">
              🖼️ 스타일 샷 (이미지) 세트
            </h2>
            <div className="rounded-[10px] bg-white p-4 md:p-5">
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <p className="text-center text-[14px] text-[#2E343D] md:text-[18px]">
                  테마 최대 4개 + AI 모델 착샷 + 항공샷 + 고스트샷
                </p>
                <p className="text-center text-[20px] font-bold text-[#2E343D] md:text-[24px]">
                  스타일 샷 세트 약 100장
                </p>
                <p className="text-[20px] font-bold text-error md:text-[24px]">
                  <span className="font-normal line-through">9,000</span>{' '}
                  900캐시
                </p>
              </div>
            </div>
          </div>

          {/* Style Clip Section */}
          <div className="flex flex-1 flex-col gap-3 md:gap-4">
            <h2 className="text-[16px] font-bold text-[#2E343D] md:text-[18px]">
              🎥 스타일 클립 (영상)
            </h2>
            <div className="rounded-[10px] bg-white p-4 md:p-5">
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <p className="text-center text-[14px] text-[#2E343D] md:text-[18px]">
                  생성된 스타일 샷으로 클릭 한 번에!
                </p>
                <div className="flex flex-col items-center gap-1 md:flex-row md:gap-2">
                  <img
                    src="/assets/img/reels_preview-7433b0.png"
                    alt="릴스 미리보기"
                    className="h-[20px] w-[100px] rounded-[4px] md:h-[29px] md:w-[151px]"
                  />
                  <p className="text-center text-[16px] font-bold text-[#2E343D] md:text-[24px]">
                    릴스(9:16) 4초 영상
                  </p>
                </div>
                <p className="text-[20px] font-bold text-error md:text-[24px]">
                  <span className="font-normal line-through">9,000</span>{' '}
                  900캐시
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cash Charging Section */}
        <div className="flex w-[343px] flex-col gap-3 rounded-[20px] bg-[#F6F8FB] p-5 md:w-[1000px] md:gap-4 md:px-8 md:pb-8 md:pt-10">
          <p className="text-left text-[16px] font-bold text-[#2E343D] md:w-[873px] md:text-left md:text-[18px]">
            충전 단위를 선택하고, {isMobile && <br />}
            <span className="text-main">보너스 캐시</span>
            까지 얻어보세요!
          </p>

          <div className="rounded-[10px] bg-white p-4 md:p-[30px]">
            <div className="flex flex-col gap-4 md:gap-5">
              {autoChargeList?.chargeMap?.total?.map((el, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex items-center justify-center gap-2 md:justify-start">
                      <p className="text-[16px] font-bold text-[#2E343D] md:text-[18px]">
                        {Number(el.chargeAmount).toLocaleString()}캐시
                      </p>
                      {Number(el.chargeAmount) === 100000 && (
                        <span className="rounded-[4px] bg-main px-2 py-1 text-[14px] font-bold text-white md:px-3 md:text-[18px]">
                          인기
                        </span>
                      )}
                    </div>
                    {Number(el.bonusRate) > 0 && (
                      <div className="flex flex-col items-center gap-1 md:items-end">
                        <p className="text-[14px] font-bold text-[#2E343D] md:text-[16px]">
                          + {el.bonus.toLocaleString()} 캐시 보너스
                        </p>
                        <div className="flex items-center gap-1">
                          <p className="text-[12px] text-[#5E6068] md:text-[14px]">
                            {autoChargeList.benefitInfo?.affiliateDescription &&
                              '제휴 혜택'}
                            {autoChargeList.benefitInfo?.inviteCode &&
                              '초대 코드'}
                            {autoChargeList.benefitType === 'purchase' &&
                              '충전 혜택'}
                          </p>
                          <span
                            className={classNames(
                              'rounded-[4px] px-1 text-[12px] font-bold md:text-[14px]',
                              {
                                'bg-[#EEF1F7] text-error':
                                  Number(el.bonusRate) <= 9,
                                'bg-error text-white': Number(el.bonusRate) > 9,
                              },
                            )}
                          >
                            {Number(el.bonusRate).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {index !== autoChargeList.chargeMap.total.length - 1 && (
                    <hr className="h-[1px] bg-[#EEF1F7]" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Auto charge benefit notice */}
            {/* <div className="mt-3 md:relative md:mt-0">
              <p className="text-center text-[12px] font-bold text-[#5E6068] md:absolute md:-top-1 md:right-[180px] md:text-[14px] md:text-white">
                자동충전 혜택은 충전시마다 매번
              </p>
            </div> */}
          </div>

          <Button
            className="w-full rounded-[60px] bg-main py-3 text-[16px] font-bold text-white md:py-4 md:text-[18px]"
            onClick={handleCashPaymentModal}
          >
            캐시 충전하기
          </Button>
        </div>

        {/* Modals */}
        {paymentModal && (
          <PaymentModal
            setPaymentModal={setPaymentModal}
            paymentOrderRequest={paymentOrderRequest}
            affiliateBenefit={affiliateBenefit}
            inviteBenefit={inviteBenefit}
            affiliatePlan={userInfo?.affiliate?.plan}
            couponBenefit={couponBenefit}
            defaultBenefit={defaultBenefit}
            setCouponBenefitData={setCouponBenefit}
            setAffiliateBenefitData={setAffiliateBenefit}
            setDefaultBenefitData={setDefaultBenefit}
            userInfo={userInfo?.userInfo}
            planId={0}
            type=""
            upgrade={false}
            monthPrice={0}
          />
        )}

        {isCashPaymentModal && (
          <CashPaymentModal
            setCashPaymentModal={setCashPaymentModal}
            userInfo={userInfo?.userInfo}
            affiliatePoint={userInfo?.affiliate?.point}
            userCurrentPoint={myPointData?.totalPoint || 0}
            requestPurchasePoint={requestPurchasePoint}
          />
        )}
      </div>
    </>
  )
}
